import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import AddressAutocomplete from './AddressAutocomplete';

// Filtri supportati (chiave URL → tipo)
const MULTI_KEYS = [
  'comune', 'tribunale', 'piano', 'piano_categoria', 'liquidita_tier',
  'livello_ristrutturazione', 'disponibilita', 'tentativo_vendita',
  'tipologia_categoria', 'tipologia_escludi',
  'piano_escludi', 'piano_categoria_escludi', 'disponibilita_escludi',
];
const BOOL_KEYS = ['dentro_gra', 'nuovi', 'scadute', 'scadenza_30gg'];
const NUM_KEYS = ['prezzo_max', 'mq_min', 'mq_max'];
// Geo filter è composto da: center_lat, center_lng, radius_km e (per UI) center_label.
// Lo conto come 1 solo filtro logico.
const GEO_KEYS = ['center_lat', 'center_lng', 'radius_km', 'center_label'];
export const FILTER_KEYS = [...MULTI_KEYS, ...BOOL_KEYS, ...NUM_KEYS, ...GEO_KEYS];

// Viste — prima erano tab sopra la tabella/mappa, ora segmented control dentro il pannello
export const VIEW_OPTIONS = [
  { id: 'valutazione', label: 'In valutazione' },
  { id: 'preferiti',   label: 'Preferiti' },
  { id: 'scartate',    label: 'Scartati' },
  { id: 'all',         label: 'Tutti' },
];
export const VIEW_DEFAULT = 'valutazione';

// Categorie catastali derivate da pattern su `tipologia`
export const TIPOLOGIA_CATEGORIES = [
  { id: 'sign',         label: 'A/1 Signorile' },
  { id: 'civ',          label: 'A/2 Civile' },
  { id: 'eco',          label: 'A/3 Economico' },
  { id: 'pop',          label: 'A/4 Popolare' },
  { id: 'villin',       label: 'A/7 Villini' },
  { id: 'ville',        label: 'A/8 Ville' },
  { id: 'appartamento', label: 'Appartamento' },
  { id: 'altro',        label: 'Altro / Non classificato' },
];

/**
 * Conta filtri attivi (escludendo verdetto/q che sono nella toolbar/pill bar).
 * Include la VIEW se diversa dal default (es. "preferiti" o "scartate").
 */
export function countActiveFilters(searchParams) {
  let n = 0;
  for (const k of FILTER_KEYS) {
    if (GEO_KEYS.includes(k)) continue; // geo conta come 1 solo
    const v = searchParams.get(k);
    if (v !== null && v !== '' && v !== 'false') n += 1;
  }
  if (searchParams.get('radius_km')) n += 1; // geo: una sola "etichetta"
  const view = searchParams.get('view') || VIEW_DEFAULT;
  if (view !== VIEW_DEFAULT) n += 1;
  return n;
}

const X = (p) => (
  <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Pill switch per scegliere modalità "Includi" vs "Escludi".
 * Stile minimal: 2 pillole affiancate, una attiva.
 */
function ModeSwitch({ mode, onChange }) {
  return (
    <span style={{
      display: 'inline-flex', gap: 0, padding: 2,
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 999, fontSize: 10.5, fontWeight: 500,
    }}>
      {['includi', 'escludi'].map((m) => (
        <button key={m} type="button" onClick={() => onChange(m)}
          style={{
            padding: '3px 10px', border: 0, borderRadius: 999, cursor: 'pointer',
            background: mode === m
              ? (m === 'escludi' ? 'oklch(0.55 0.18 25)' : 'var(--fg)')
              : 'transparent',
            color: mode === m ? 'var(--bg-elev)' : 'var(--fg-muted)',
            textTransform: 'capitalize',
            transition: 'background 0.12s, color 0.12s',
          }}>
          {m}
        </button>
      ))}
    </span>
  );
}

/**
 * Sezione filtro che gestisce includi/escludi su due chiavi diverse,
 * mostrando una sola lista di chip + uno switch di modalità.
 *
 *   includeKey: chiave URL per i valori inclusi (es. 'tipologia_categoria')
 *   excludeKey: chiave URL per i valori esclusi (es. 'tipologia_escludi')
 *   options:    array {id, label}
 */
function IncludeExcludeSection({
  title, includeKey, excludeKey, options, getMulti, setMulti, params,
}) {
  const includeVals = getMulti(includeKey);
  const excludeVals = getMulti(excludeKey);
  // Modalità iniziale: se ci sono valori "escludi" mostra escludi, altrimenti includi
  const [mode, setMode] = useState(() => excludeVals.length > 0 && includeVals.length === 0 ? 'escludi' : 'includi');
  // Resta sincronizzato con i params se cambiano da fuori
  useEffect(() => {
    if (excludeVals.length > 0 && includeVals.length === 0) setMode('escludi');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const currentKey = mode === 'includi' ? includeKey : excludeKey;
  const otherKey   = mode === 'includi' ? excludeKey : includeKey;
  const currentVals = mode === 'includi' ? includeVals : excludeVals;

  // Quando l'utente switcha modalità, sposto i valori sull'altra chiave
  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    const valsToMove = mode === 'includi' ? includeVals : excludeVals;
    setMulti(currentKey, []);
    if (valsToMove.length > 0) setMulti(otherKey, valsToMove);
    setMode(newMode);
  };

  const toggle = (v) => {
    const i = currentVals.indexOf(String(v));
    const next = [...currentVals];
    if (i >= 0) next.splice(i, 1); else next.push(String(v));
    setMulti(currentKey, next);
  };

  return (
    <section className="fp__section">
      <h3 className="fp__section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{title}</span>
        <ModeSwitch mode={mode} onChange={handleModeChange} />
      </h3>
      <div className="fp__chips">
        {options.map((o) => (
          <MultiChip
            key={o.id}
            value={o.id}
            label={o.label}
            selected={currentVals.includes(String(o.id))}
            onToggle={toggle}
          />
        ))}
      </div>
    </section>
  );
}

function MultiChip({ value, selected, onToggle, label }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(value)}
      className="fp__chip"
      data-on={selected ? 'true' : 'false'}
    >
      {label ?? value}
    </button>
  );
}

function ToggleRow({ checked, onChange, label, hint }) {
  return (
    <label className="fp__toggle">
      <div className="fp__toggle-text">
        <div className="fp__toggle-lbl">{label}</div>
        {hint && <div className="fp__toggle-hint">{hint}</div>}
      </div>
      <span className="fp__switch" data-on={checked ? 'true' : 'false'}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
        />
        <span className="fp__switch-knob" />
      </span>
    </label>
  );
}

export default function FilterPanel({ open, onClose, searchParams, setSearchParams, filterOptions, context = 'immobili' }) {
  // Contesto "lead" = stiamo configurando i CRITERI DI RICERCA di un cliente.
  // Alcuni filtri (vista, "solo nuovi", "scadute", comune-multi-pick, tribunale)
  // sono view-specific per la pagina immobili e non hanno senso come criterio
  // permanente di un lead → li nascondiamo.
  const isLeadCtx = context === 'lead';
  const ref = useRef(null);
  const params = searchParams || new URLSearchParams();
  const centerLabel = params.get('center_label') || '';

  // === Geo state (DECLARED BEFORE early return — rules of hooks!) ===
  const [geoSearch, setGeoSearch] = useState(centerLabel);
  const [geoResults, setGeoResults] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const getMulti = (key) => {
    const v = params.get(key);
    return v ? v.split(',').filter(Boolean) : [];
  };
  const setMulti = (key, values) => {
    const next = new URLSearchParams(params);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(','));
    setSearchParams(next, { replace: true });
  };
  const toggleMulti = (key) => (v) => {
    const curr = getMulti(key);
    const i = curr.indexOf(String(v));
    if (i >= 0) curr.splice(i, 1);
    else curr.push(String(v));
    setMulti(key, curr);
  };
  const setSingle = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === '' || value == null || value === false) next.delete(key);
    else next.set(key, String(value));
    setSearchParams(next, { replace: true });
  };
  const resetAll = () => {
    const next = new URLSearchParams(params);
    FILTER_KEYS.forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const activeCount = countActiveFilters(params);
  const opts = filterOptions || {};

  // Derived geo values (state già dichiarato in cima)
  const centerLat = params.get('center_lat');
  const centerLng = params.get('center_lng');
  const radiusKm = parseInt(params.get('radius_km') || '0', 10);
  const geoActive = !!(centerLat && centerLng && radiusKm);

  const handleGeocode = async () => {
    const q = geoSearch.trim();
    if (q.length < 2) return;
    setGeoLoading(true);
    setGeoResults([]);
    try {
      const { data } = await api.get('/api/geocode', { params: { q } });
      setGeoResults(data.results || []);
    } catch {
      setGeoResults([]);
    } finally {
      setGeoLoading(false);
    }
  };

  const pickGeoResult = (r) => {
    const next = new URLSearchParams(params);
    next.set('center_lat', String(r.lat));
    next.set('center_lng', String(r.lng));
    next.set('center_label', r.display_name);
    if (!next.get('radius_km')) next.set('radius_km', '10');
    setSearchParams(next, { replace: true });
    setGeoResults([]);
    setGeoSearch(r.display_name);
  };

  const updateRadius = (v) => {
    if (!centerLat) return;
    const next = new URLSearchParams(params);
    next.set('radius_km', String(v));
    setSearchParams(next, { replace: true });
  };

  const clearGeo = () => {
    const next = new URLSearchParams(params);
    GEO_KEYS.forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    setGeoSearch('');
    setGeoResults([]);
  };

  return (
    <div className="fp__backdrop" onClick={onClose}>
      <aside
        className="fp__panel"
        onClick={(e) => e.stopPropagation()}
        ref={ref}
        role="dialog"
        aria-label="Filtri"
      >
        <header className="fp__head">
          <div>
            <div className="fp__title">Filtri</div>
            <div className="fp__sub">{activeCount > 0 ? `${activeCount} attivi` : 'Nessun filtro attivo'}</div>
          </div>
          <button type="button" onClick={onClose} className="fp__close" aria-label="Chiudi">
            <X size={16} />
          </button>
        </header>

        <div className="fp__body">
          {/* === Visualizzazione (segmented control: era una toolbar di tab) === */}
          {!isLeadCtx && (
          <section className="fp__section">
            <h3 className="fp__section-title">Visualizzazione</h3>
            <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              {VIEW_OPTIONS.map((v) => {
                const current = params.get('view') || VIEW_DEFAULT;
                const active = current === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSingle('view', v.id === VIEW_DEFAULT ? '' : v.id)}
                    aria-pressed={active}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      fontSize: 12,
                      fontWeight: active ? 600 : 500,
                      background: active ? 'var(--bg-elev)' : 'transparent',
                      color: active ? 'var(--fg)' : 'var(--fg-muted)',
                      border: active ? '1px solid var(--border)' : '1px solid transparent',
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'background 0.1s, color 0.1s',
                    }}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
          </section>
          )}

          {/* === Zona di ricerca (geo radius) === */}
          <section className="fp__section">
            <h3 className="fp__section-title">
              Zona di ricerca
              {geoActive && (
                <button type="button" onClick={clearGeo}
                  style={{
                    fontSize: 10, color: 'var(--fg-muted)',
                    background: 'transparent', border: 0, cursor: 'pointer',
                    textTransform: 'none', letterSpacing: 0,
                  }}>
                  ✕ rimuovi
                </button>
              )}
            </h3>
            <div className="fp__field">
              <label className="fp__lbl">Indirizzo, città o regione</label>
              <AddressAutocomplete
                value={geoSearch}
                onChange={setGeoSearch}
                onSelect={({ lat, lng, label }) => {
                  pickGeoResult({ lat, lng, display_name: label });
                }}
              />
            </div>

            {geoActive && (
              <>
                <div style={{
                  padding: '8px 10px', background: 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 8,
                  fontSize: 12, marginBottom: 10,
                }}>
                  📍 {centerLabel || `${centerLat}, ${centerLng}`}
                </div>
                <div className="fp__field" style={{ marginBottom: 0 }}>
                  <label className="fp__lbl" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Raggio</span>
                    <span style={{ fontWeight: 600, color: 'var(--fg)' }}>{radiusKm} km</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={50}
                    step={1}
                    value={radiusKm}
                    onChange={(e) => updateRadius(e.target.value)}
                    style={{ width: '100%' }}
                  />
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: 10, color: 'var(--fg-subtle)', marginTop: -4,
                  }}>
                    <span>1 km</span>
                    <span>25 km</span>
                    <span>50 km</span>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* === Toggles === */}
          <section className="fp__section">
            <h3 className="fp__section-title">Opzioni rapide</h3>
            <ToggleRow
              checked={params.get('dentro_gra') === 'true'}
              onChange={(v) => setSingle('dentro_gra', v)}
              label="Solo dentro GRA"
              hint="Roma — solo immobili dentro il Grande Raccordo Anulare"
            />
            {!isLeadCtx && (
              <>
                <ToggleRow
                  checked={params.get('nuovi') === 'true'}
                  onChange={(v) => setSingle('nuovi', v)}
                  label="Solo nuovi (ultime 24h)"
                  hint="Immobili aggiunti nelle ultime 24 ore"
                />
                <ToggleRow
                  checked={params.get('scadenza_30gg') === 'true'}
                  onChange={(v) => setSingle('scadenza_30gg', v)}
                  label="Con scadenza entro 30 gg"
                  hint="Aste con data vendita nei prossimi 30 giorni"
                />
                <ToggleRow
                  checked={params.get('scadute') === 'true'}
                  onChange={(v) => setSingle('scadute', v)}
                  label="Mostra anche scadute"
                  hint="Per default le aste passate sono nascoste"
                />
              </>
            )}
          </section>

          {/* === Range === */}
          <section className="fp__section">
            <h3 className="fp__section-title">Prezzo &amp; metratura</h3>
            <div className="fp__field">
              <label className="fp__lbl">Offerta minima ≤ (€)</label>
              <input
                type="number"
                inputMode="numeric"
                className="fp__input"
                placeholder="es. 150000"
                value={params.get('prezzo_max') || ''}
                onChange={(e) => setSingle('prezzo_max', e.target.value)}
                min={0}
              />
            </div>
            <div className="fp__range">
              <div className="fp__field">
                <label className="fp__lbl">Mq min</label>
                <input
                  type="number"
                  inputMode="numeric"
                  className="fp__input"
                  placeholder="50"
                  value={params.get('mq_min') || ''}
                  onChange={(e) => setSingle('mq_min', e.target.value)}
                  min={0}
                />
              </div>
              <div className="fp__field">
                <label className="fp__lbl">Mq max</label>
                <input
                  type="number"
                  inputMode="numeric"
                  className="fp__input"
                  placeholder="200"
                  value={params.get('mq_max') || ''}
                  onChange={(e) => setSingle('mq_max', e.target.value)}
                  min={0}
                />
              </div>
            </div>
          </section>

          {/* === Tipologia catastale (con switch includi/escludi) === */}
          <IncludeExcludeSection
            title="Tipologia catastale"
            includeKey="tipologia_categoria"
            excludeKey="tipologia_escludi"
            options={TIPOLOGIA_CATEGORIES}
            getMulti={getMulti}
            setMulti={setMulti}
            params={params}
          />

          {/* === Liquidità tier === */}
          {/* Marina 2026-07-10: nascosto temporaneamente ("non ha senso" per gli
              utenti). Per riattivarlo: rimuovere `false &&` qui sotto. */}
          {false && opts.liquidita_tier?.length > 0 && (
            <section className="fp__section">
              <h3 className="fp__section-title">Liquidità</h3>
              <div className="fp__chips">
                {opts.liquidita_tier.map((v) => (
                  <MultiChip
                    key={v}
                    value={v}
                    label={v}
                    selected={getMulti('liquidita_tier').includes(String(v))}
                    onToggle={toggleMulti('liquidita_tier')}
                  />
                ))}
              </div>
            </section>
          )}

          {/* === Ristrutturazione === */}
          {opts.livello_ristrutturazione?.length > 0 && (
            <section className="fp__section">
              <h3 className="fp__section-title">Livello ristrutturazione</h3>
              <div className="fp__chips">
                {opts.livello_ristrutturazione.map((v) => (
                  <MultiChip
                    key={v}
                    value={v}
                    label={v}
                    selected={getMulti('livello_ristrutturazione').includes(String(v))}
                    onToggle={toggleMulti('livello_ristrutturazione')}
                  />
                ))}
              </div>
            </section>
          )}

          {/* === Tentativo di vendita (1° / 2° / 3° / …) === */}
          {opts.tentativo_vendita?.length > 0 && (
            <section className="fp__section">
              <h3 className="fp__section-title">Tentativo di vendita</h3>
              <div className="fp__chips">
                {opts.tentativo_vendita.map((v) => (
                  <MultiChip
                    key={v}
                    value={v}
                    label={`${v}°`}
                    selected={getMulti('tentativo_vendita').includes(String(v))}
                    onToggle={toggleMulti('tentativo_vendita')}
                  />
                ))}
              </div>
            </section>
          )}

          {/* === Disponibilità (con switch includi/escludi) === */}
          {opts.disponibilita?.length > 0 && (
            <IncludeExcludeSection
              title="Disponibilità"
              includeKey="disponibilita"
              excludeKey="disponibilita_escludi"
              options={opts.disponibilita.map((v) => ({ id: v, label: v }))}
              getMulti={getMulti}
              setMulti={setMulti}
              params={params}
            />
          )}

          {/* === Piano categoria (semi-interrato / basso / medio / alto / attico) === */}
          {opts.piano_categoria?.length > 0 && (
            <IncludeExcludeSection
              title="Piano"
              includeKey="piano_categoria"
              excludeKey="piano_categoria_escludi"
              options={opts.piano_categoria.map((o) => ({ id: o.value, label: o.label }))}
              getMulti={getMulti}
              setMulti={setMulti}
              params={params}
            />
          )}

          {/* === Comune (scrollable list) === */}
          {!isLeadCtx && opts.comune?.length > 0 && (
            <section className="fp__section">
              <h3 className="fp__section-title">
                Comune <span className="fp__count">{getMulti('comune').length}/{opts.comune.length}</span>
              </h3>
              <div className="fp__chips fp__chips--scroll">
                {opts.comune.map((v) => (
                  <MultiChip
                    key={v}
                    value={v}
                    label={v}
                    selected={getMulti('comune').includes(String(v))}
                    onToggle={toggleMulti('comune')}
                  />
                ))}
              </div>
            </section>
          )}

          {/* === Tribunale === */}
          {!isLeadCtx && opts.tribunale?.length > 0 && (
            <section className="fp__section">
              <h3 className="fp__section-title">
                Tribunale <span className="fp__count">{getMulti('tribunale').length}/{opts.tribunale.length}</span>
              </h3>
              <div className="fp__chips fp__chips--scroll">
                {opts.tribunale.map((v) => (
                  <MultiChip
                    key={v}
                    value={v}
                    label={v}
                    selected={getMulti('tribunale').includes(String(v))}
                    onToggle={toggleMulti('tribunale')}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        <footer className="fp__foot">
          <button type="button" className="btn btn--ghost" onClick={resetAll} disabled={activeCount === 0}>
            Reset
          </button>
          <button type="button" className="btn btn--solid" onClick={onClose}>
            Mostra risultati
          </button>
        </footer>
      </aside>
    </div>
  );
}

